import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@shared/state/AuthContext'

/**
 * Where Microsoft sends people back to.
 *
 * supabase-js does the work: `detectSessionInUrl` finds the code in the query
 * string, exchanges it for a session and fires SIGNED_IN, which reaches this
 * page through AuthContext. All this page does is wait for that to settle and
 * then step aside — and clean the code out of the address bar on the way, so a
 * reload or a shared link does not try to redeem a code that is already spent.
 *
 * Entra can also refuse — consent declined, the account not assigned to the
 * application — and says so in `error_description` rather than by failing. In
 * that case there is no session to wait for, so the reason is carried back to
 * the login screen, which has somewhere to show it.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate()
  const { session, loading } = useAuth()

  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search + '&' + window.location.hash.replace(/^#/, ''),
    )
    const failure = params.get('error_description') || params.get('error')
    if (failure) {
      navigate('/login', { replace: true, state: { error: failure } })
      return
    }

    if (loading) return

    if (session) {
      window.history.replaceState({}, '', '/auth/callback')
      navigate('/', { replace: true })
      return
    }

    // Settled with nothing to show for it: the code was missing, already
    // spent, or rejected. Back to the front door rather than a blank page.
    navigate('/login', {
      replace: true,
      state: { error: 'That sign-in did not complete. Please try again.' },
    })
  }, [navigate, session, loading])

  return <div className="auth-gate" />
}
